import { MapPin, MapVendor, PinPlotEvent, RouteType } from "@mcleod/components";
import { AutogenLayoutFeasibilityMap } from "./autogen/AutogenLayoutFeasibilityMap";
import { CityUtil, DateUtil, LogManager, MapSettings, ModelRow } from "@mcleod/core";

const log = LogManager.getLogger("dispatch/asset/FeasibilityMap");

export interface FeasibilityMapProps {
    mapData: ModelRow;
}

interface RouteOptions {
    routeData: any[];
    routeType: RouteType;
    hideMarkers?: boolean;
    routeId?: string;
    routeColor?: string;
    showGoogleRoute?: boolean;
    showProposedRoute?: boolean;
    disableToggleButtons?: boolean;
}

export class FeasibilityMap extends AutogenLayoutFeasibilityMap implements FeasibilityMapProps {
    mapData: ModelRow<any>;

    onLoad(): void | Promise<void> {
        this.mapFeasibility.addPinPlotListener((event: PinPlotEvent) => this.formatMapPin(event));
    }

    async createRoute() {
        await this.mapFeasibility.ensureLoaded();
        const routeDataPreassign = [];
        const routeDataCurrent = [];
        const routeDataPCMILER = [];
        const pickupLatLong = this.mapData?.get("pickup_latlong");
        if (pickupLatLong) {
            routeDataPreassign.push({ latitude: pickupLatLong.latitude, longitude: pickupLatLong.longitude });
            routeDataCurrent.push({ latitude: pickupLatLong.latitude, longitude: pickupLatLong.longitude });
        }
        const stops = this.mapData.get("intermediate_stops");
        if (stops && stops.length > 0) {
            stops.forEach((stop) => {
                const latitude = stop.intermediate_latitude;
                const longitude = stop.intermediate_longitude;
                const pin = new MapPin(latitude, longitude);
                routeDataPreassign.push({ latitude: latitude, longitude: longitude });
                pin.data = new ModelRow(this.sourceFeasibility.url, false, stop);
                pin.data.set("data_type", "intermediate");
                this.mapFeasibility.addPin(pin);
             });
        }
        const deliveryLatLong = this.mapData?.get("delivery_latlong");
        if (deliveryLatLong) {
            routeDataPreassign.push({ latitude: deliveryLatLong.latitude, longitude: deliveryLatLong.longitude });
        }
        const finalLatLong = this.mapData?.get("final_stop_latlong");
        this.plotPins("final_stop");
        this.plotPins("delivery");
        this.plotPins("pickup");
        if (finalLatLong) {
            routeDataCurrent.push({ latitude: finalLatLong.latitude, longitude: finalLatLong.longitude });
        }
        const routing_points = this.mapData?.get("routing_points");
        const vendor = this.mapFeasibility.vendor;
        if (routing_points && routing_points.length > 0 && MapVendor.GOOGLE === vendor) {
            this.labelPracticalDistanceVendor.text = this.mapData.get("practical_distance_vendor");
            routing_points?.forEach((point) => {
                routeDataPCMILER.push({ lat: point.latitude, lng: point.longitude });
            });
        }
        switch (vendor) {
            case MapVendor.TRIMBLE:
                // For Trimble, we use the first set for the preassign route and second for current route
                this.createMapRoute({
                    routeData: routeDataPreassign,
                    routeType: RouteType.Trimble,
                    hideMarkers: true,
                    routeId: "currentRoute"
                });
                this.createMapRoute({
                    routeData: routeDataCurrent,
                    routeType: RouteType.Trimble,
                    hideMarkers: true,
                    routeId: "finalRoute"
                });
                break;
            case MapVendor.GOOGLE:
                // For Google, we use MileMaker for PC*MILER data, and Google for preassign/current
                this.createMapRoute({
                    routeData: routeDataPCMILER,
                    routeType: RouteType.MileMaker,
                    hideMarkers: true,
                    routeId: "mileMarker",
                    routeColor: MapSettings.getSingleton().getRouteColor(MapSettings.MAP_VENDOR.GOOGLE, MapSettings.ROUTE_VENDOR.MILEMAKER),
                    showGoogleRoute: false,
                    showProposedRoute: true,
                    disableToggleButtons: true
                });
                this.createMapRoute({
                    routeData: routeDataPreassign,
                    routeType: RouteType.Google,
                    hideMarkers: true,
                    routeId: "googlePreassign",
                    routeColor: MapSettings.getSingleton().getRouteColor(MapSettings.MAP_VENDOR.GOOGLE, MapSettings.ROUTE_VENDOR.GOOGLE),
                    showGoogleRoute: true,
                    showProposedRoute: false,
                    disableToggleButtons: true
                });
                this.createMapRoute({
                    routeData: routeDataCurrent,
                    routeType: RouteType.Google,
                    hideMarkers: true,
                    routeId: "googleCurrent",
                    routeColor: MapSettings.getSingleton().getRouteColor(MapSettings.MAP_VENDOR.GOOGLE, MapSettings.ROUTE_VENDOR.GOOGLE),
                    showGoogleRoute: true,
                    showProposedRoute: false,
                    disableToggleButtons: true
                });
                break;
            default:
                log.error(`Unknown or unsupported map vendor: ${this.mapFeasibility.vendor}`);
                // When all else fails, default to Google; this is the most likely substitute for any other vendor
                // as it supports a variety of data formats and is widely used. It also is the vendor most likely
                // to not be resolved at this point in the code.
                this.createMapRoute({
                    routeData: routeDataPreassign,
                    routeType: RouteType.Google,
                    hideMarkers: true,
                    routeColor: MapSettings.getSingleton().getRouteColor(MapSettings.MAP_VENDOR.GOOGLE, MapSettings.ROUTE_VENDOR.UNKNOWN),
                    showGoogleRoute: true,
                    showProposedRoute: false,
                    disableToggleButtons: true
                });
                this.createMapRoute({
                    routeData: routeDataCurrent,
                    routeType: RouteType.Google,
                    hideMarkers: true,
                    routeColor: MapSettings.getSingleton().getRouteColor(MapSettings.MAP_VENDOR.GOOGLE, MapSettings.ROUTE_VENDOR.GOOGLE),
                    showGoogleRoute: true,
                    showProposedRoute: false,
                    disableToggleButtons: true
                });
                break;
        }
        this.mapFeasibility.fitPins();
    }

    private createMapRoute(options: RouteOptions): void {
        this.mapFeasibility.createRoute(
            options.routeData,
            options.routeType,
            options.hideMarkers ?? false,
            options.routeId,
            options.routeColor,
            options.showProposedRoute,
            options.disableToggleButtons
        );
    }

    plotPins(prefix: string) {
        const data = this.mapData;
        const latLong = data.get(`${prefix}_latlong`);
        const pin = new MapPin(latLong?.latitude, latLong?.longitude);
        pin.data = data;
        pin.data.set("data_type", prefix);
        this.mapFeasibility.addPin(pin);
    }

    private formatMapPin(event: PinPlotEvent) {
        const pin: MapPin = event.pin;
        const dataType = pin.data.get("data_type");
        const datesString: string = pin.data?.get(`${dataType}_sched_early`);
        const dates: Date = new Date(datesString);
        let formattedDates: string;
        if (dates != null) {
            formattedDates = DateUtil.formatDateTime(dates, DateUtil.getUserDateTimeFormat());
        } else {
            formattedDates = datesString;
        }
        pin.image = pin.data?.get(`${dataType}_map_marker`) ? pin.data?.get(`${dataType}_map_marker`): "map-pin-white-dot-small";
        pin.tooltip = FeasibilityMap.buildStopTooltip(pin, formattedDates, dataType);
        pin.clusterable = false;
    }

    private static buildStopTooltip(pin: MapPin, formattedDates: string, dataType: string): string {
        const stopType = pin.data?.get(`${dataType}_stop_type`) + " - ";
        const stopStatus = "Status: " + pin.data?.get(`${dataType}_status`) + "\n";
        const stopLocationInfo = "Stop Name: " + pin.data?.get(`${dataType}_location_name`) + "\n";
        const stopCityStateZip = CityUtil.formatCityStateZip(pin.data?.get(`${dataType}_city_name`),
            pin.data?.get(`${dataType}_state`), pin.data?.get(`${dataType}_zip`)) + "\n";
        const dateTime = `Date/Time: ${formattedDates}` + "\n";
        const destMiles = pin.data?.get(`${dataType}_move_dist`, null) ? `Dist Previous: ${pin.data?.get(`${dataType}_move_dist`, 0)}`: "";
        return `${stopType}${stopStatus}${stopLocationInfo}${stopCityStateZip}${dateTime}${destMiles}`;
    }
}
